import classNames from 'classnames';
import React, { FC, memo, useCallback } from 'react';
import { Box } from 'entities/Box';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { RentOptionDocument } from 'entities/RentOption';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useGetBoxesSuggestionQuery } from '../api/getBoxesSuggestion';
import { BoxRow } from './BoxRow';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
import { Popup } from 'shared/ui/Popup';
import { UserAccountType } from 'entities/User';

interface BoxesContainerProps {
  sizeCodeId: string;
  selectedBoxId: string | undefined;
  rentDiscounts: RentOptionDocument[];
  generalDiscounts: RentOptionDocument[];
  onSelectBox: (box: Box | undefined, sizeCodeId?: string) => void;
  onBookBox: (box: Box, sizeCodeId: string) => void;
  accountType?: Nullable<UserAccountType>;
}

export const BoxesContainer: FC<BoxesContainerProps> = memo((props) => {
  const { sizeCodeId, selectedBoxId, rentDiscounts, generalDiscounts, onSelectBox, onBookBox, accountType } = props;

  const { t } = useAppTranslation('booking');

  const { data } = useGetBoxesSuggestionQuery(sizeCodeId, { skip: !sizeCodeId });

  const selectBox = useCallback(
    (box: Box): void => {
      onSelectBox(box, sizeCodeId);
    },
    [onSelectBox, sizeCodeId],
  );

  const bookBox = useCallback(
    (box: Box): void => {
      onBookBox(box, sizeCodeId);
    },
    [onBookBox, sizeCodeId],
  );

  const hasRentDiscounts = rentDiscounts.length > 0;
  const hasGeneralDiscounts = generalDiscounts.filter(({ showAsPromotion }) => showAsPromotion).length > 0;

  // const discountsLabel = useMemo(() => {
  //   const rentDiscountsLabel = rentDiscounts.map((discount) => `“${getLocalizedString(discount.label)}“`).join(', ');

  //   const generalDiscountsLabel = generalDiscounts
  //     .filter((discount) => discount.showAsPromotion)
  //     .map((discount) => `“${getLocalizedString(discount.label)}“`)
  //     .join(', ');

  //   const needComma = rentDiscountsLabel && generalDiscountsLabel;

  //   return `${rentDiscountsLabel}${needComma ? ', ' : ''}${generalDiscountsLabel}`;
  // }, [generalDiscounts, rentDiscounts]);

  const discountRentOptions = [...generalDiscounts, ...rentDiscounts];

  return (
    <div className={classNames('relative', { 'min-h-[307px]': !data?.content.length })}>
      {discountRentOptions.length > 0 && (
        <div className="bg-accent text-white rounded-b-lg px-2 py-1 text-center">
          <div className="flex flex-wrap justify-center items-center desktop:gap-2">
            <span className="whitespace-nowrap text-xs desktop:text-base">{t('This size is discounted the')}</span>

            <div className="flex flex-wrap justify-center desktop:gap-2">
              {discountRentOptions.map((discount, index) => {
                const isLast = index === discountRentOptions.length - 1;
                return (
                  <div key={discount.label} className="flex items-center gap-1">
                    <span className="text-xs desktop:text-base">{`“${getLocalizedString(discount.label)}“`}</span>
                    {discount?.extendedDescription && (
                      <Popup
                        description={
                          <div
                            className="max-w-[310px] desktop:max-w-[600px]"
                            dangerouslySetInnerHTML={{ __html: getLocalizedString(discount.extendedDescription) }}
                          />
                        }
                      >
                        <InfoIcon width={22} height={22} className="[&>path]:stroke-white cursor-pointer flex-shrink-0" />
                      </Popup>
                    )}
                    {!isLast && ','}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      <div className={classNames('desktop:p-4', { 'blur-[3px] pointer-events-none': data?.noAvailableBoxes })}>
        {Boolean(data?.content.length) &&
          data?.content.map((box) => (
            <BoxRow
              key={box.boxId}
              box={box}
              selectedBoxId={selectedBoxId}
              hasRentDiscounts={hasRentDiscounts}
              hasGeneralDiscounts={hasGeneralDiscounts}
              onSelectBox={selectBox}
              onBookBox={bookBox}
              accountType={accountType}
            />
          ))}
      </div>
    </div>
  );
});
